//#region [IMPORT]

// === THIRD-PARTY ===
import React, { createContext, useContext, useEffect, useState } from "react"
import { useParams } from 'react-router';
import { Outlet } from "react-router-dom";

// === CONTANTS ===
import { RULE_STATUS } from "constants/general";

// === API ===
import { on, removeListener, sendMessageData } from "api/prokey/prokey.websocket";

//#endregion

const ContextAnalytics = createContext();

const review_options = {
  FAILED    : { label: 'Failed'   , value: RULE_STATUS.FAILED    },
  PASSED    : { label: 'Passed'   , value: RULE_STATUS.PASSED    },
  OVERRULED : { label: 'Overruled', value: RULE_STATUS.OVERRULED },
};

const DOCUMENT_COMPARE_METHODS = {
  open: (a, b) => {
    return (a || 0) - (b || 0);
  },
  closed: (a, b) => {
    return (a || 0) - (b || 0);
  },
  total: (a, b) => {
    return (a || 0) - (b || 0);
  },
  status: (a, b) => {
    return (a || 0) - (b || 0);
  },
  version: (a, b) => {
    const cmp_a = (a || '').toUpperCase();
    const cmp_b = (b || '').toUpperCase();
    return cmp_a < cmp_b ? -1 : cmp_a > cmp_b ? 1 : 0;
  },
  name: (a, b) => {
    const cmp_a = (a || '').toUpperCase();
    const cmp_b = (b || '').toUpperCase();
    return cmp_a < cmp_b ? -1 : cmp_a > cmp_b ? 1 : 0;
  },
};

//-----------------------------------------------------------------
export function AnalyticsProvider( { children } ) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const route_params = useParams();
  const file_id = parseInt( route_params.file_id );
  
  const [   file_name, setFileName   ] = useState( '' );
  const [       stats, setStats      ] = useState( { open: '-', closed: '-', total: '-'} );
  const [   documents, setDocuments  ] = useState( [] );
  const [       rules, setRules      ] = useState( [] );
  const [review_flags, setReviewFlags] = useState( [RULE_STATUS.FAILED]);
  const [  is_loading, setIsLoading  ] = useState(false);

  const [ sort_header , setSortHeader ] = useState( 'status' );
  const [ sort_ascend , setSortAscend ] = useState( true );
  const [ document_current, setDocumentCurrent ] = useState( '' );

  const [ task, setTask ] = useState( null );

  const values = {
    file_id, file_name,
    documents, setDocuments,
    document_current, setDocumentCurrent,
    rules, stats,
    task, clearTask,
    review_flags, setReviewFlags, review_options,
    is_loading  , setIsLoading  ,
    getPrevDocument, getNextDocument,
    sort_header, setSortHeader,
    sort_ascend, setSortAscend,
  };

  //#endregion

  //#region [EFFECT]
  
  useEffect( () => {
    //-----------------------------------------------------------------
    function listener_file(data) {
      //-----------------------------------------------------------------
      if ( data === null ) {
        // navigate('/analytics/files');
        return;
      }

      setFileName ( data.loan_number );
      setRules    ( data.rules || [] );
      setStats    ( { open: data.num_open, closed: data.num_closed } );

      setIsLoading( false );
    }
    //-----------------------------------------------------------------    
    function handleTask(data) {
      //-----------------------------------------------------------------
      setTask(data);
      setFileName( data?.file_name );
      if( data?.numcode ) setDocumentCurrent( data?.numcode );
    }

    on( 'FILE'       , listener_file );
    on( 'AUDIT-TASK' , handleTask    );
    
    return () => {
      removeListener( 'FILE'        , listener_file );
      removeListener( 'AUDIT-TASK'  , handleTask    );
    };
  }, [] ); // empty dependency = called only once at startup
  
  useEffect( () => {
    return () => {
      sendMessageData( 'FILE_ANALYTICS_CLOSE', { file_id } )
    }
  }, [file_id] )
  
  useEffect(() => {
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []); // empty dependency = called only once at startup

  useEffect( () => {
    sendMessageData( 'FILE-RULE-DOCUMENTS-REQUEST', { file_id } );
  }, [file_id] );

  useEffect( () => {
    //-----------------------------------------------------------------
    function handleFileRuleDocuments(data) {
      //-----------------------------------------------------------------
      data.sort((a, b) => DOCUMENT_COMPARE_METHODS[sort_header](a[sort_header], b[sort_header]));
      if( !sort_ascend ) data.reverse();
      setDocuments( data );
    }

    on( 'FILE-RULE-DOCUMENTS', handleFileRuleDocuments );

    documents.sort((a, b) => DOCUMENT_COMPARE_METHODS[sort_header](a[sort_header], b[sort_header]));
    if( !sort_ascend ) documents.reverse();

    setDocuments( [...documents] );

    return () => {
      removeListener( 'FILE-RULE-DOCUMENTS', handleFileRuleDocuments );
    }
  }, [file_id, sort_header, sort_ascend] );

  //#endregion
  
  //#region [EVENTS]

  //-----------------------------------------------------------------
  function onKeyDown(event) {
    //-----------------------------------------------------------------
    if(false) {}
    // Comparison Image
    else if ( event.key === 'PageUp' && event.shiftKey ) {
      event.preventDefault();
      event.stopPropagation();
    }
    else if ( event.key === 'PageDown' && event.shiftKey ) {
      event.preventDefault();
      event.stopPropagation();
    }
    // Primary Image
    else if ( event.key === 'PageUp' ) {
      event.preventDefault();
      event.stopPropagation();
    }
    else if ( event.key === 'PageDown' ) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  //#endregion

  //#region [MODIFY]

  function clearTask() {
    setTask( null );
  }

  //-----------------------------------------------------------------
  function getPrevDocument() {
    //-----------------------------------------------------------------
    const index_curr = documents.findIndex( x => x.numcode === document_current );
    const index_prev = index_curr === 0 ? documents.length - 1 : index_curr - 1;
    const document_prev = documents[index_prev];

    if( document_prev?.numcode ) setDocumentCurrent( document_prev.numcode );
  }

  //-----------------------------------------------------------------
  function getNextDocument() {
    //-----------------------------------------------------------------
    const index_curr = documents.findIndex( x => x.numcode === document_current );
    const index_next = index_curr >= documents.length - 1 ? 0 : index_curr + 1;
    const document_next = documents[index_next];

    if( document_next?.numcode ) setDocumentCurrent( document_next.numcode );
  }

  //#endregion


  return (
    <ContextAnalytics.Provider value={ values }>
      <Outlet />
    </ContextAnalytics.Provider>
  );
};

//-----------------------------------------------------------------
export function useAnalytics() {
  //-----------------------------------------------------------------
  return useContext( ContextAnalytics );
}